import { useState } from 'react';
import ThreadDetails from '../../components/threads/thread-details/ThreadDetails';
import { toastNotification } from '../../components/toast/Toast';
import { useUserContext } from '../../providers/UserProvider';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { useCreateInternalCommentReply } from '../../queries/use-create-internal-comment-reply';
import { useInternalComment } from '../../queries/use-internal-comment';
import { useUpdateInternalCommentReply } from '../../queries/use-update-internal-comment-reply';
import { useUpdateInternalComments } from '../../queries/use-update-internal-comments';
import { InternalComment, InternalCommentStatusEnum } from '../../types/internal-comment';
import InternalCommentReplyDeleteConfirm from './InternalCommentReplyDeleteConfirm';
import InternalCommentStatus from './InternalCommentStatus';
import { getThreadContext } from './utils';

type EditInternalCommentProps = {
    internalCommentId: string;
    workpaperId: string;
    onClose: () => void;
    onDelete: (id: string) => void;
    onResolve: (id: string) => void;
    onActive: (id: string) => void;
    onEdit: (internalComment: InternalComment) => void;
};

function EditInternalComment({
    workpaperId,
    internalCommentId,
    onClose,
    onDelete,
    onResolve,
    onActive,
    onEdit,
}: EditInternalCommentProps): JSX.Element {
    const internalCommentQuery = useInternalComment(workpaperId, internalCommentId);
    const [deleteReplyId, setDeleteReplyId] = useState<string | undefined>();
    const { id } = useUserContext();
    const { readOnly } = useWorkpaperContext();

    function handleActionClick(action: string): void {
        if (action === 'ACTIVE') {
            onActive(internalCommentId);
        }
        if (action === 'RESOLVE') {
            onResolve(internalCommentId);
        }
        if (action === 'DELETE') {
            onDelete(internalCommentId);
        }
        if (action === 'EDIT') {
            onEdit(internalComment);
        }
    }

    const createInternalCommentReplyQuery = useCreateInternalCommentReply(workpaperId, internalCommentId);
    const updateInternalCommentReplyQuery = useUpdateInternalCommentReply(workpaperId, internalCommentId);
    const updateInternalCommentQuery = useUpdateInternalComments(workpaperId);

    function resolveComment(): void {
        updateInternalCommentQuery.mutate([{ id: internalCommentId, fields: { status: InternalCommentStatusEnum.RESOLVED } }], {
            onSuccess: () => {
                toastNotification('success', 'Internal comment marked as resolved!');
            },
        });
    }

    function handleReply(rawReply: string, structuredReply: string, setResolve: boolean): void {
        createInternalCommentReplyQuery.mutate(
            {
                reply: { rawReply, structuredReply: JSON.parse(structuredReply) },
            },
            {
                onSuccess: () => {
                    if (setResolve) {
                        resolveComment();
                    }
                    toastNotification('success', 'New internal comment reply created!');
                },
            }
        );
    }

    function handleReplyEdit(replyId: string, rawReply: string, structuredReply: string, setResolve?: boolean): void {
        updateInternalCommentReplyQuery.mutate(
            {
                id: replyId,
                rawReply,
                structuredReply: JSON.parse(structuredReply),
            },
            {
                onSuccess: () => {
                    if (setResolve) {
                        resolveComment();
                    }
                    toastNotification('success', 'Internal comment reply updated!');
                },
            }
        );
    }

    if (internalCommentQuery.isLoading) {
        return <></>;
    }

    if (!internalCommentQuery.isSuccess) {
        return <></>;
    }

    const { internalComment } = internalCommentQuery.data;

    return (
        <>
            {deleteReplyId && (
                <InternalCommentReplyDeleteConfirm
                    internalCommentId={internalCommentId}
                    workpaperId={workpaperId}
                    replyId={deleteReplyId}
                    onClose={(): void => setDeleteReplyId(undefined)}
                    onDelete={(): void => {
                        setDeleteReplyId(undefined);
                    }}
                />
            )}

            <ThreadDetails
                structuredContent={internalComment.structuredComment ? JSON.stringify(internalComment.structuredComment) : undefined}
                createdAt={internalComment.createdAt}
                createdBy={internalComment.createdBy}
                onClose={onClose}
                canReply={internalComment.status === InternalCommentStatusEnum.ACTIVE}
                replies={internalComment.replies}
                statusBadge={<InternalCommentStatus status={internalComment.status} />}
                actions={[
                    {
                        label: 'Mark as resolved',
                        value: 'RESOLVE',
                        hidden: internalComment.status === InternalCommentStatusEnum.RESOLVED,
                    },
                    {
                        label: 'Mark as active',
                        value: 'ACTIVE',
                        hidden: internalComment.status === InternalCommentStatusEnum.ACTIVE,
                    },
                    {
                        label: 'Delete',
                        value: 'DELETE',
                        hidden: internalComment.status === InternalCommentStatusEnum.RESOLVED || internalComment.createdBy.id !== id,
                    },
                    {
                        label: 'Edit',
                        value: 'EDIT',
                        hidden: internalComment.status === InternalCommentStatusEnum.RESOLVED || internalComment.createdBy.id !== id,
                    },
                ]}
                onActionsClick={handleActionClick}
                onReply={handleReply}
                onReplyEdit={handleReplyEdit}
                onReplyDelete={setDeleteReplyId}
                context={getThreadContext(internalComment)}
                disableAllActions={readOnly}
            />
        </>
    );
}
export default EditInternalComment;
